import { MeasurementQuantityEnum } from 'domains/measurementDomain';
import {
  FilterCriteriaItem,
  FilterObj,
  PageParam,
  ValueName,
} from './appDomain';

export interface SvcType {
  id: number;
  name: string;
  revxId: number;
  exclusiveType: ExclusiveTypeEum;
  version: number;
  createAt: string;
  updateAt: string;
}

export enum ExclusiveTypeEum {
  ANY_OF = 'ANY_OF',
  ONE_OF = 'ONE_OF',
  CUSTOMIZE = 'CUSTOMIZE',
}

export interface SvcSpec {
  id: number;
  revxId: number;
  baseRefId: number;
  svcId: number;
  description: string;
  parameters: SvcParameter[];
  makereadyCostUsd: number;
  costUsd: number;
  markupPct: number;
  materialCostUsd: number;
  materialMarkupPct: number;
  materialWidthIn: number;
  materialLengthIn: number;
  materialHeightIn: number;
  materialThicknessIn: number;
  maxTotalCaliperIn: number;
  timeHr: number;
  makereadyTimeHr: number;
  leadTimeHr: number;
  createAt: string;
  updateAt: string;
  active: boolean;
  version: number;
}

export interface SvcParameter {
  label: string;
  parameter: string;
}

export interface Svc {
  id: number;
  revxId: number;
  isBase: boolean;
  baseRefId: number;
  orgId: number;
  svcType: SvcType;
  svcSpecs: SvcSpec[];
  svcOptions: SvcOption[];
  name: string;
  showInstructions: boolean;
  message: string;
  hasMaterial: boolean;
  uom: number;
  uomCoefficient: UOMCoefficientEnum;
  materialUom: number;
  materialUomCoefficient: UOMCoefficientEnum;
  jobConfigViewTypes: JobConfigViewTypeEnum[];
  parameterLabels: string[];
  image: string;
  createAt: string;
  updateAt: string;
  active: boolean;
  version: number;
}

export enum JobConfigViewTypeEnum {
  BUYER = 'BUYER',
  EXPERT = 'EXPERT',
}
export interface SvcOption {
  label: string;
  values: string[];
  defaultValue: string;
  type: ServiceOptionTypeEnum;
  /**
   * What this option's values measure, if anything. Absent on options saved before the field
   * existed, which is treated as NONE - shown exactly as entered.
   */
  valueUom?: MeasurementQuantityEnum;
}

type SvcOptionValueWithId = {
  id: string;
  value: string;
};
export interface SvcOptionWithId extends SvcOption {
  id: string;
  withIdValues: SvcOptionValueWithId[];
}

export enum ServiceOptionTypeEnum {
  RADIO = 'RADIO',
  CHECKBOX = 'CHECKBOX',
  INPUT = 'INPUT',
}

export const ServiceOptionTypeDisplay: {
  [key in ServiceOptionTypeEnum]: string;
} = {
  [ServiceOptionTypeEnum.CHECKBOX]: 'Multi Select',
  [ServiceOptionTypeEnum.RADIO]: 'Single Select',
  [ServiceOptionTypeEnum.INPUT]: 'Text Input',
};

export const ServiceOptionTypeOptions = Object.keys(
  ServiceOptionTypeDisplay
).map((key) => {
  return {
    value: key,
    name: ServiceOptionTypeDisplay[key as ServiceOptionTypeEnum],
  };
});

export enum UOMCoefficientEnum {
  JOB_QUANTITY = 'JOB_QUANTITY',
  BUNDLE_COUNT = 'BUNDLE_COUNT',
  ITEM_SQ_IN = 'ITEM_SQ_IN',
  JOB_COUNT = 'JOB_COUNT',
  OM_SERVICE_QUANTITY = 'OM_SERVICE_QUANTITY',
  PRESS_SHEET_COUNT = 'PRESS_SHEET_COUNT',
  LINEAR_IN = 'LINEAR_IN',
  PARCEL_COUNT = 'PARCEL_COUNT',
}

export const UOMCoefficientDisplay: {
  [key in UOMCoefficientEnum]: string;
} = {
  [UOMCoefficientEnum.JOB_QUANTITY]: 'Job Quantity',
  [UOMCoefficientEnum.BUNDLE_COUNT]: 'Bundle Count',
  [UOMCoefficientEnum.ITEM_SQ_IN]: 'Item Sq In',
  [UOMCoefficientEnum.JOB_COUNT]: 'Job Count',
  [UOMCoefficientEnum.OM_SERVICE_QUANTITY]: 'OM Service Quantity',
  [UOMCoefficientEnum.PRESS_SHEET_COUNT]: 'Press Sheet Count',
  [UOMCoefficientEnum.LINEAR_IN]: 'Linear In',
  [UOMCoefficientEnum.PARCEL_COUNT]: 'Parcel Count',
};

export const UOMCoefficientOptions = Object.keys(UOMCoefficientDisplay).map(
  (key) => {
    return {
      value: key,
      name: UOMCoefficientDisplay[key as UOMCoefficientEnum],
    };
  }
);

export interface ListSvcParam {
  page: PageParam;
  query?: SvcFilter;
}

export const UOMOptions: ValueName[] = [
  {
    value: 1,
    name: 'Per Ea',
  },
  {
    value: 125,
    name: 'Per 125',
  },
  {
    value: 1000,
    name: 'Per 1000',
  },
  {
    value: 2500,
    name: 'Per 2500',
  },
  {
    value: 5000,
    name: 'Per 5000',
  },
];

export const getUomValue = (
  label: string,
  uomCoefficient: UOMCoefficientEnum
) => {
  if (
    uomCoefficient === 'JOB_QUANTITY' ||
    uomCoefficient === 'BUNDLE_COUNT' ||
    uomCoefficient === 'PRESS_SHEET_COUNT' ||
    uomCoefficient === 'JOB_COUNT' ||
    uomCoefficient === 'OM_SERVICE_QUANTITY' ||
    uomCoefficient === 'PARCEL_COUNT'
  ) {
    const lbl = label.replace('Per ', '');

    if (lbl === 'Ea') {
      return 1;
    } else {
      return Number(lbl);
    }
  }

  if (uomCoefficient === 'ITEM_SQ_IN' || uomCoefficient === 'LINEAR_IN') {
    return 1;
  }
};

export const getUomLabel = (value: number) => {
  let label = 'Per ';
  if (value === 1) {
    return (label += 'Ea');
  } else {
    return (label += value);
  }
};

export interface SvcFilter extends FilterObj {
  svcRevxId?: FilterCriteriaItem;
  specRevxId?: FilterCriteriaItem;
  name?: FilterCriteriaItem;
  description?: FilterCriteriaItem;
  svcTypeId?: FilterCriteriaItem;
  costUsd?: FilterCriteriaItem;
  makereadyCostUsd?: FilterCriteriaItem;
  makereadyTimeHr?: FilterCriteriaItem;
  markupPct?: FilterCriteriaItem;
  materialCostUsd?: FilterCriteriaItem;
  materialMarkupPct?: FilterCriteriaItem;
  timeHr?: FilterCriteriaItem;
  uom?: FilterCriteriaItem;
  uomCoefficient?: FilterCriteriaItem;
}

export interface BaseSvcCreateParams {
  revxId: number;
  isBase: boolean;
  baseRefId: number;
  orgId: number;
  svcTypeId: number;
  svcOptions: SvcOption[];
  name: string;
  showInstructions: boolean;
  message: string;
  hasMaterial: boolean;
  uom: number;
  uomCoefficient: UOMCoefficientEnum;
  materialUom: number;
  materialUomCoefficient: UOMCoefficientEnum;
  jobConfigViewTypes: string[];
  parameterLabels: string[];
  image: string;
}

export interface SvcUpdateParams extends BaseSvcCreateParams {
  id: number;
  active: boolean;
  version: number;
  options: SvcOption[];
}

export interface SvcSpecCreateParams {
  revxId: number;
  svcId: number;
  description: string;
  parameters: SvcParameter[];
  makereadyCostUsd: number;
  costUsd: number;
  markupPct: number;
  materialCostUsd: number;
  materialMarkupPct: number;
  materialWidthIn: number;
  materialLengthIn: number;
  materialHeightIn: number;
  materialThicknessIn: number;
  maxTotalCaliperIn: number;
  timeHr: number;
  makereadyTimeHr: number;
  leadTimeHr: number;
  active: boolean;
}

export interface SvcSpecUpdateParams extends SvcSpecCreateParams {
  id: number;
  version: number;
}

export const EMPTY_BASE_SVC: Partial<Svc> = {
  isBase: true,
  svcSpecs: [],
  svcOptions: [],
  name: '',
  showInstructions: false,
  message: '',
  hasMaterial: false,
  uom: 1,
  uomCoefficient: UOMCoefficientEnum.JOB_QUANTITY,
  jobConfigViewTypes: [JobConfigViewTypeEnum.BUYER],
  parameterLabels: [],
  image: '',
  active: false,
};

export const EMPTY_BASE_SPEC: Partial<SvcSpec> = {
  description: '',
  parameters: [],
  makereadyCostUsd: 0,
  costUsd: 0,
  markupPct: 0,
  materialCostUsd: 0,
  materialMarkupPct: 0,
  materialWidthIn: 0,
  materialLengthIn: 0,
  materialHeightIn: 0,
  materialThicknessIn: 0,
  maxTotalCaliperIn: 0,
  timeHr: 0,
  makereadyTimeHr: 0,
  leadTimeHr: 0,
  active: false,
};
